import { z } from 'zod';
import { RULE_FIELDS, RULE_OPERATORS } from '../import/rules.js';
import { centsSchema, cuidSchema, plainDateSchema } from './common.js';
/**
 * Contratos da Fase 3: importacao de extrato e regras de categorizacao.
 */
export const importSourceSchema = z.enum(['OFX', 'CSV']);
export const importStatusSchema = z.enum(['PREVIEW', 'APPLIED', 'DISCARDED']);
/**
 * Situacao de cada linha conferida.
 *
 *  - NEW        entra como lancamento novo;
 *  - DUPLICATE  ja esta no sistema pelo identificador — nao entra (regra 4);
 *  - RECONCILE  parece ser um lancamento manual que ja existe; a pessoa decide;
 *  - IGNORED    o usuario mandou pular.
 */
export const importRowStatusSchema = z.enum(['NEW', 'DUPLICATE', 'RECONCILE', 'IGNORED']);
export const importRowSchema = z.object({
    id: z.string(),
    lineNumber: z.number().int(),
    date: z.string(),
    description: z.string(),
    amountCents: z.string(),
    kind: z.enum(['INCOME', 'EXPENSE']),
    status: importRowStatusSchema,
    /** Preenchido quando a linha ja existe: aponta o lancamento que a barrou. */
    duplicateOfTransactionId: z.string().nullable(),
    reconcileTransactionId: z.string().nullable(),
    reconcileDescription: z.string().nullable(),
    reconcileDate: z.string().nullable(),
    reconcileScore: z.number().nullable(),
    suggestedCategoryId: z.string().nullable(),
    suggestedCategoryName: z.string().nullable(),
    matchedRuleName: z.string().nullable(),
    /** Aviso em texto para a pessoa ler antes de confirmar. */
    warning: z.string().nullable(),
});
export const importCountsSchema = z.object({
    total: z.number().int(),
    novos: z.number().int(),
    duplicados: z.number().int(),
    conciliar: z.number().int(),
    ignorados: z.number().int(),
});
export const importBatchSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    accountName: z.string(),
    source: importSourceSchema,
    filename: z.string(),
    status: importStatusSchema,
    counts: importCountsSchema,
    /** Periodo coberto pelo arquivo, quando ele informa. */
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
    /** Linhas que o arquivo trouxe e nao deu para ler. */
    problems: z.array(z.string()),
    createdAt: z.string(),
    appliedAt: z.string().nullable(),
    importedCount: z.number().int(),
    reconciledCount: z.number().int(),
});
export const importPreviewSchema = z.object({
    batch: importBatchSchema,
    rows: z.array(importRowSchema),
});
export const importListSchema = z.object({ batches: z.array(importBatchSchema) });
/**
 * Decisao por linha na hora de confirmar.
 *
 * Quem nao mandar decisao nenhuma aceita o que a conferencia sugeriu. Mandar
 * a lista inteira e o caminho da tela; mandar so as linhas alteradas tambem
 * funciona.
 */
export const importDecisionSchema = z
    .object({
    rowId: cuidSchema,
    action: z.enum(['IMPORT', 'SKIP', 'RECONCILE']),
    categoryId: cuidSchema.nullable().optional(),
    reconcileTransactionId: cuidSchema.optional(),
})
    .superRefine((value, ctx) => {
    if (value.action === 'RECONCILE' && !value.reconcileTransactionId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reconcileTransactionId'],
            message: 'Para conciliar e preciso dizer com qual lancamento.',
        });
    }
});
export const commitImportSchema = z.object({
    decisions: z.array(importDecisionSchema).max(5000).default([]),
});
export const commitImportResultSchema = z.object({
    batch: importBatchSchema,
    imported: z.number().int(),
    reconciled: z.number().int(),
    skipped: z.number().int(),
});
/** Mapeamento manual de colunas, para CSV que nenhum perfil reconheceu. */
export const csvColumnMapSchema = z.object({
    date: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(80),
    amount: z.string().trim().max(80).optional(),
    debit: z.string().trim().max(80).optional(),
    credit: z.string().trim().max(80).optional(),
    externalId: z.string().trim().max(80).optional(),
    decimal: z.enum(['dot', 'comma', 'auto']).default('comma'),
});
export const csvProfileSchema = z.object({
    id: z.string(),
    label: z.string(),
    hint: z.string(),
});
export const csvProfileListSchema = z.object({ profiles: z.array(csvProfileSchema) });
// -------------------------------------------------------------- regras
export const ruleFieldSchema = z.enum(RULE_FIELDS);
export const ruleOperatorSchema = z.enum(RULE_OPERATORS);
export const ruleConditionInputSchema = z
    .object({
    field: ruleFieldSchema,
    operator: ruleOperatorSchema,
    value: z.string().trim().max(200).optional(),
    amountMinCents: centsSchema.optional(),
    amountMaxCents: centsSchema.optional(),
})
    .superRefine((value, ctx) => {
    const textOperators = ['CONTAINS', 'EQUALS', 'STARTS_WITH', 'ENDS_WITH', 'REGEX'];
    const isAmount = value.field === 'AMOUNT';
    if (isAmount) {
        if (textOperators.includes(value.operator) && value.operator !== 'EQUALS') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['operator'],
                message: 'Para valor, use maior que, menor que, entre ou igual a.',
            });
        }
        // Cada operador le um campo especifico. Uma regra salva com o campo
        // errado nao "nao casa": ela LANCA na hora de avaliar, e como o motor
        // roda em toda importacao, uma unica regra torta derrubaria todo envio
        // dali em diante. Barrar aqui e a diferenca entre um erro de formulario
        // e um sistema que para de importar extrato.
        const needsMin = ['GREATER_THAN', 'EQUALS', 'BETWEEN'].includes(value.operator);
        const needsMax = ['LESS_THAN', 'BETWEEN'].includes(value.operator);
        if (needsMin && value.amountMinCents === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['amountMinCents'],
                message: 'Informe o valor da condicao.',
            });
        }
        if (needsMax && value.amountMaxCents === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['amountMaxCents'],
                message: 'Informe o valor maximo da condicao.',
            });
        }
        if (value.amountMinCents !== undefined &&
            value.amountMaxCents !== undefined &&
            value.amountMinCents > value.amountMaxCents) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['amountMaxCents'],
                message: 'O valor maximo precisa ser maior que o minimo.',
            });
        }
        return;
    }
    if (!textOperators.includes(value.operator)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['operator'],
            message: 'Para texto, use contem, igual a, comeca com, termina com ou expressao.',
        });
    }
    if (!value.value) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'Informe o texto da condicao.',
        });
    }
});
export const createRuleSchema = z.object({
    name: z.string().trim().min(1).max(80),
    priority: z.coerce.number().int().min(0).max(9999).default(100),
    enabled: z.boolean().default(true),
    kind: z.enum(['INCOME', 'EXPENSE']).nullable().default(null),
    categoryId: cuidSchema.nullable().default(null),
    tagIds: z.array(cuidSchema).max(10).default([]),
    conditions: z.array(ruleConditionInputSchema).min(1).max(5),
});
export const updateRuleSchema = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    priority: z.coerce.number().int().min(0).max(9999).optional(),
    enabled: z.boolean().optional(),
    kind: z.enum(['INCOME', 'EXPENSE']).nullable().optional(),
    categoryId: cuidSchema.nullable().optional(),
    tagIds: z.array(cuidSchema).max(10).optional(),
    conditions: z.array(ruleConditionInputSchema).min(1).max(5).optional(),
});
export const ruleSchema = z.object({
    id: z.string(),
    name: z.string(),
    priority: z.number().int(),
    enabled: z.boolean(),
    kind: z.enum(['INCOME', 'EXPENSE']).nullable(),
    categoryId: z.string().nullable(),
    categoryName: z.string().nullable(),
    tags: z.array(z.object({ id: z.string(), name: z.string(), colorToken: z.string() })),
    conditions: z.array(z.object({
        field: ruleFieldSchema,
        operator: ruleOperatorSchema,
        value: z.string().nullable(),
        amountMinCents: z.string().nullable(),
        amountMaxCents: z.string().nullable(),
    })),
    /** Quantas vezes a regra ja decidiu uma categoria. Ajuda a achar regra morta. */
    appliedCount: z.number().int(),
    /**
     * Nasceu sozinha, de uma categoria escolhida a mao num lancamento.
     *
     * A tela separa as aprendidas das escritas para que dê para apagar em bloco
     * o que o sistema deduziu sem tocar no que a pessoa montou de proposito.
     */
    learned: z.boolean(),
    /** O recebedor que deu origem a ela. */
    learnedKey: z.string().nullable(),
    createdAt: z.string(),
});
export const ruleListSchema = z.object({ rules: z.array(ruleSchema) });
/**
 * Reaplicar as regras ao que ja esta no banco.
 *
 * Por padrao so mexe no que esta SEM categoria: reescrever categoria que a
 * pessoa escolheu na mao seria apagar trabalho dela.
 */
export const applyRulesSchema = z.object({
    onlyUncategorized: z.boolean().default(true),
    accountId: cuidSchema.optional(),
    from: plainDateSchema.optional(),
    to: plainDateSchema.optional(),
    /** Simulacao: conta quantos mudariam, sem gravar. */
    dryRun: z.boolean().default(false),
});
export const applyRulesResultSchema = z.object({
    matched: z.number().int(),
    updated: z.number().int(),
    dryRun: z.boolean(),
    /**
     * A varredura tem teto. Quando ele e atingido, a tela precisa dizer isso:
     * um "12 lancamentos categorizados" silencioso, com 3.000 fora da conta,
     * se le como "acabou" quando nao acabou.
     */
    limitReached: z.boolean(),
});
//# sourceMappingURL=import.js.map